import { defineStore } from "pinia";
import { useGeneralStore } from "%/stores/general-store";
import { useStoryStore } from "%/stores/story-store";
import { useConfigurationStore } from "%/stores/configuration-store";
import useFanPublicationStore from "%/stores/fan-publication-store";
import { useMyPublicationPostFeedStore } from "~/smart-feed/stores/feed/my-publication-post-feed-store";
import dayjs from "dayjs";
import {
  STORY_PUBLISH_STATUS_ACTIVE,
  STORY_PUBLISH_STATUS_DRAFT,
  STORY_PUBLISH_STATUS_SCHEDULED,
  STORY_PUBLISH_STATUS_SUBMITTED_FOR_REVIEW,
  STORY_PUBLISH_STATUS_NEEDS_EDITING,
  STORY_PUBLISH_STATUS_SCHEDULED_NEEDS_REVIEW,
} from "~/constants/story-status-constants";
import { STORY_VARIANT_TYPE_STORY } from "@/constants/story-variant-type-constants";
import { PUBLISHING_PLATFORM_TYPE } from "%/constants/story-constants";

export const useDynamicUgcStore = defineStore("dynamic-ugc-store", {
  state: () => ({
    title: "Untitled Story",
    story: {},
    hashtags: [],
    savingStatus: "All Changes Saved",
    loadingHashtags: false,
    loadingTitle: false,
    loadingPublish: false,
    loadingCreate: false,
    replaceUrlWithEditUrl: false,
    debounceTitle: null,
    debounceUpdate: null,
    updateStoryExclusivityIsLoading: false,
    updateStoryRequiresSyndicationApprovalContentIsLoading: false,
    updateStoryAllowRemixContentIsLoading: false,
    isStoryExclusive: false,
    isStoryAllowRemix: false,
    isStoryRequiresSyndicationApproval: false,
    uploadThumbnailLoading: false,
  }),
  getters: {
    orgId() {
      return useGeneralStore().orgId;
    },
    userId() {
      return useGeneralStore().userId;
    },
    isUserLoggedIn() {
      return useConfigurationStore().isSignupAndLoginEnabled && this.userId > 0;
    },
    storyId() {
      return this.story?.story_id;
    },
    isCurrentUserAuthorOfArticle() {
      return this.story?.authoring_user_id === this.userId;
    },
    generalStore() {
      return useGeneralStore();
    },
    segments() {
      const segmentArray = Object.values(this.story.segments);
      return this.story.segment_order.reduce((acc, curr) => {
        const segment = segmentArray.find(seg => seg.story_segment_id === curr.story_segment_id);
        return [
          ...acc,
          segment,
        ];
      }, []);
    },
  },
  actions: {
    setTitle(title) {
      this.title = title;
      clearTimeout(this.debounceTitle);
      this.savingStatus = "Unsaved changes";
      this.debounceTitle = setTimeout(() => {
        this.updateTitle();
      }, 1000);
    },
    async uploadThumbnail(storyId, file) {
      this.uploadThumbnailLoading = true;
      return useStoryStore().uploadStoryThumbnail(storyId, file)
          .then(response => {
            this.story.thumbnail = response.data.thumbnail;
          })
          .finally(() => {
            this.uploadThumbnailLoading = false;
          });
    },
    removeThumbnail(storyId) {
      this.uploadThumbnailLoading = true;
      useStoryStore().removeStoryThumbnail(storyId, response => {
        this.story.thumbnail = response.thumbnail;
      }, () => {
        this.uploadThumbnailLoading = false;
      });
    },
    uploadImageFunction(file) {
      return useStoryStore().uploadStoryImage(file);
    },
    uploadVideoFunction(file) {
      return useStoryStore().uploadStoryVideo(file);
    },
    sortHashtags(responseHashtags) {
      let sortedHashtags = [];
      this.hashtags.forEach(tag => {
        const foundTag = responseHashtags.find(responseTag => responseTag.hashtag === tag.hashtag);
        if (foundTag) {
          sortedHashtags.push(foundTag);
        }
      });
      responseHashtags.forEach(tag => {
        if (!sortedHashtags.find(sortedTag => sortedTag.hashtag === tag.hashtag)) {
          sortedHashtags.push(tag);
        }
      });
      return sortedHashtags;
    },
    addHashtags(hashtags, callback = () => {
    }) {
      this.hashtags.push(hashtags);
      this.loadingHashtags = true;
      this.savingStatus = "Saving...";
      this.apiPost(`/story/${this.storyId}/tag`, { hashtags }, response => {
        callback(true);
        this.hashtags = this.sortHashtags(response.hashtags);
        this.loadingHashtags = false;
        this.savingStatus = "All Changes Saved";
      }, () => {
        callback(false);
        this.savingStatus = "Error Saving Changes";
        this.loadingHashtags = false;
      });
    },
    removeHashtag({ el, index }, callback = () => {
    }) {
      this.hashtags.splice(index, 1);
      const removedTagId = el.hashtag_id;
      this.loadingHashtags = true;
      this.savingStatus = "Saving...";
      this.apiPost(`/story/${this.storyId}/untag`, { hashtag_id: removedTagId }, response => {
        callback(true);
        this.savingStatus = "All Changes Saved";
        this.hashtags = this.sortHashtags(response.hashtags);

        this.loadingHashtags = false;
      }, () => {
        callback(false);
        this.savingStatus = "Error Saving Changes";
        this.loadingHashtags = false;
      });
    },
    updateTitle(callback = () => {
    }) {
      this.loadingTitle = true;
      this.savingStatus = "Saving...";
      this.apiPost(`/story/${this.storyId}/title`, {
        story_title: this.title,
      }, () => {
        this.loadingTitle = false;
        this.savingStatus = "All Changes Saved";
        callback(true);
      }, () => {
        this.loadingTitle = false;
        this.savingStatus = "Error Saving Changes";
        callback(false);
      });
    },
    create(
      callback = () => {},
      redirect = true,
      variantType = STORY_VARIANT_TYPE_STORY,
      platformType = PUBLISHING_PLATFORM_TYPE.PUBLICATION,
      platformId = null,
      socialAccountId = null,
    ) {
      if (!this.generalStore.isLoggedIn) {
        this.generalStore.setRedirectLocation(window.location.href);
        this.generalStore.redirectTo("/login");
        return;
      }
      this.loadingCreate = true;
      const publishingPlatformId = platformId === null || platformId === undefined
        ? useConfigurationStore().publicationId
        : platformId;
      this.apiPost("/story/create",
        {
          org_id: this.orgId,
          user_id: this.userId,
          publishing_platform_id: publishingPlatformId,
          publishing_platform_type: platformType,
          variant_type: variantType,
          social_account_id: socialAccountId,
        },
        response => {
          callback(response);
          if (redirect) {
            window.location.href = `${useFanPublicationStore().fanUrl}/edit-article/${response.story_id}`;
          }
          this.loadingCreate = false;
        },
        () => {
          callback(false);
          this.loadingCreate = false;
        });
    },
    update(segments, callback = () => {
    }) {
      clearTimeout(this.debounceUpdate);
      this.savingStatus = "Unsaved Changes";
      this.debounceUpdate = setTimeout(() => {
        this.savingStatus = "Saving...";
        this.apiPost(`/story/${this.storyId}`, {
          "story_version_id": this.story.current_story_version_id,
          "live_segment_order": segments.liveSegmentOrder,
          "segments": segments.segments,
        }, response => {
          callback(true);
          this.story = {
            ...this.story,
            ...response,
          };
          this.savingStatus = "All Changes Saved";
          this.loadingUpdate = false;
        }, () => {
          callback(true);
          this.savingStatus = "Error Saving Changes";
          this.loadingUpdate = false;
        });
      }, 1000);
    },
    publishArticle(callback = () => {
    }, redirect = true) {
      this.loadingPublish = true;
      this.apiPost(`/story/${this.storyId}/publish`, {}, () => {
        callback(true);
        this.loadingPublish = false;
        this.story.status = STORY_PUBLISH_STATUS_ACTIVE;
        const feedStore = useMyPublicationPostFeedStore();
        const filters = feedStore.filters;
        feedStore.reload({ filters });
        if (redirect) {
          window.location.href = `${useFanPublicationStore().fanUrl}/story/${this.storyId}`;
        }
      }, () => {
        callback(false);
        this.loadingPublish = false;
      });
    },
    submitArticleForReview(callback = () => {
    }, redirect = true) {
      this.loadingPublish = true;
      this.apiPost(`/story/${this.storyId}/submit/review`, {}, () => {
        callback(true);
        this.loadingPublish = false;
        this.story.status = STORY_PUBLISH_STATUS_SUBMITTED_FOR_REVIEW;
        const feedStore = useMyPublicationPostFeedStore();
        const filters = feedStore.filters;
        feedStore.reload({ filters });
        if (redirect) {
          window.location.href = `${useFanPublicationStore().fanUrl}/story/${this.storyId}/edit`;
        }
      }, () => {
        callback(false);
        this.loadingPublish = false;
      });
    },
    scheduleArticle(dateTime, callback = () => {
    }, redirect = true) {
      this.loadingPublish = true;
      this.apiPost(`/story/${this.storyId}/${dateTime}/schedule/publish`, {}, () => {
        callback(true);
        this.loadingPublish = false;
        this.story.status = STORY_PUBLISH_STATUS_SCHEDULED;
        const feedStore = useMyPublicationPostFeedStore();
        const filters = feedStore.filters;
        feedStore.reload({ filters });
        if (redirect) {
          window.location.href = `${useFanPublicationStore().fanUrl}/story/${this.storyId}/edit`;
        }
      }, () => {
        callback(false);
        this.loadingPublish = false;
      });
    },
    scheduleArticleNeedsReview(dateTime, callback = () => {
    }, redirect = true) {
      this.loadingPublish = true;
      this.apiPost(`/story/${this.storyId}/${dateTime}/review/schedule`, {}, () => {
        callback(true);
        this.loadingPublish = false;
        this.story.status = STORY_PUBLISH_STATUS_SCHEDULED_NEEDS_REVIEW;
        const feedStore = useMyPublicationPostFeedStore();
        const filters = feedStore.filters;
        feedStore.reload({ filters });
        if (redirect) {
          window.location.href = `${useFanPublicationStore().fanUrl}/story/${this.storyId}/edit`;
        }
      }, () => {
        callback(false);
        this.loadingPublish = false;
      });
    },
    unpublishArticle(storyId, callback = () => {
    }, redirect = true) {
      this.loadingPublish = true;
      this.apiPost(`/story/${storyId}/unpublish`, {}, () => {
        callback(true);
        this.loadingPublish = false;
        this.story.status = STORY_PUBLISH_STATUS_DRAFT;
        this.savingStatus = "All Changes Saved";
        const feedStore = useMyPublicationPostFeedStore();
        const filters = feedStore.filters;
        feedStore.reload({ filters });
        if (redirect) {
          window.location.href = `${useFanPublicationStore().fanUrl}/edit-article/${storyId}`;
        }
      }, () => {
        callback(false);
        this.loadingPublish = false;
      });
    },
    rejectArticleAfterReview(callback = () => {
    }, redirect = true) {
      this.loadingPublish = true;
      this.apiPost(`/story/${this.storyId}/review/reject`, {}, () => {
        callback(true);
        this.loadingPublish = false;
        this.story.status = STORY_PUBLISH_STATUS_NEEDS_EDITING;
        const feedStore = useMyPublicationPostFeedStore();
        const filters = feedStore.filters;
        feedStore.reload({ filters });
        if (redirect) {
          window.location.href = `${useFanPublicationStore().fanUrl}/story/${this.storyId}/edit`;
        }
      }, () => {
        callback(false);
        this.loadingPublish = false;
      });
    },
    updateStoryExclusiveContentFlag(successHandler = () => {
    }, errorHandler) {
      this.updateStoryExclusivityIsLoading = true;
      this.apiPost(`/story/${this.storyId}/exclusive`, {
        exclusive: !this.isStoryExclusive ? 1 : 0,
      }, () => {
        this.isStoryExclusive = !this.isStoryExclusive;
        this.story.exclusive = this.isStoryExclusive;
        this.story.timestamp_updated = dayjs().unix();
        this.updateStoryExclusivityIsLoading = false;
        successHandler(true);
      }, () => {
        errorHandler(false);
      }, () => {
        this.updateStoryExclusivityIsLoading = false;
      });
    },
    updateStoryAllowRemixContentFlag(successHandler = () => {
    }, errorHandler = () => {}) {
      this.updateStoryAllowRemixContentIsLoading = true;
      this.apiPost(`/story/${this.storyId}/allow-remix`, {
        allow_remix: !this.isStoryAllowRemix ? 1 : 0,
      }, data => {
        this.isStoryAllowRemix = !this.isStoryAllowRemix;
        this.story.allow_remix = this.isStoryAllowRemix;
        this.story.timestamp_updated = data.timestamp_updated;
        successHandler(true);
      }, () => {
        errorHandler(false);
      }, () => {
        this.updateStoryAllowRemixContentIsLoading = false;
      });
    },
    updateStoryRequiresSyndicationApprovalContentFlag(successHandler = () => {
    }, errorHandler) {
      this.updateStoryRequiresSyndicationApprovalContentIsLoading = true;
      this.apiPost(`/story/${this.storyId}/requires-syndication-approval`, {
        requires_syndication_approval: !this.isStoryRequiresSyndicationApproval ? 1 : 0,
      }, data => {
        this.isStoryRequiresSyndicationApproval = !this.isStoryRequiresSyndicationApproval;
        this.story.requires_syndication_approval = this.isStoryRequiresSyndicationApproval;
        this.story.timestamp_updated = data.timestamp_updated;
        successHandler(true);
      }, () => {
        errorHandler(false);
      }, () => {
        this.updateStoryRequiresSyndicationApprovalContentIsLoading = false;
      });
    },
  },
});
